import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { BaseIntegrationEntity } from '@vy/types';
import { isEmptyArray, isEmptyString, isNotEmptyArray, isNotNullOrUndefined } from '@vy/utils';
import { useMemo } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useNetSuiteProducts } from '../../../../../../../hooks/requests/integrations';
import { createNetSuiteIntegrationEntity } from '../../../../../../../utils/integration-entity-creators';
import type { FormattedIntegrationProduct } from '../../../../../../../utils/types/formatted-integration-product';

type NetSuiteProductsDropdownProps<FormType extends FieldValues> = {
  fieldPath: FieldPath<FormType>;
  control: Control<FormType>;
  showDropdown: boolean;
  customerId?: string;
  valueAsId?: boolean;
  placeholder?: string;
  onChange?: (value: BaseIntegrationEntity | null) => void;
};

export function NetSuiteProductsDropdown<FormType extends FieldValues>({
  fieldPath,
  control,
  showDropdown,
  customerId,
  valueAsId,
  placeholder = 'Select NetSuite Product',
  onChange,
}: NetSuiteProductsDropdownProps<FormType>) {
  const { data: { products } = {}, isLoading } = useNetSuiteProducts(customerId, {
    enabled: showDropdown,
    suspense: false,
  });

  const formattedProducts = useMemo((): FormattedIntegrationProduct[] => {
    if (isEmptyArray(products)) {
      return [];
    }

    return products.map(({ id, name }) => ({
      id,
      name,
    }));
  }, [products]);

  return (
    <Controller
      control={control}
      name={fieldPath}
      render={({ field }) => {
        const currentValue = isNotEmptyArray(field.value) ? field.value?.at(0) : null;

        const canFindProduct = isNotNullOrUndefined(currentValue) && isNotEmptyArray(formattedProducts);

        const matchingProduct = canFindProduct
          ? formattedProducts.find((product) => product.id === currentValue.integrationExternalId)
          : null;

        return (
          <Autocomplete
            disabled={!showDropdown || isLoading}
            options={formattedProducts}
            loading={isLoading}
            multiple={false}
            getOptionLabel={(option) => option.name}
            getOptionDisabled={(option) => option.id === (valueAsId ? field.value : field.value?.id)}
            getOptionKey={(option) => option.id}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={matchingProduct}
            onChange={(_, value) => {
              const newEntity = createNetSuiteIntegrationEntity(value);
              field.onChange(newEntity ? [newEntity] : null);
              onChange?.(newEntity ?? null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={'NetSuite Products'}
                placeholder={placeholder}
                size="medium"
                slotProps={{
                  ...params.slotProps,
                  inputLabel: { shrink: true },

                  input: {
                    ...params.slotProps.input,
                    endAdornment: (
                      <>
                        {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.slotProps.input.endAdornment}
                      </>
                    ),
                  }
                }}
                onChange={(e) => {
                  if (isEmptyString(e.target.value)) {
                    field.onChange(undefined);
                  }
                }}
              />
            )}
          />
        );
      }}
    />
  );
}
